import { HttpLink, ApolloClient, InMemoryCache, from, fromPromise, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from 'apollo-link-error';
import { RetryLink } from '@apollo/client/link/retry';

import { datadogRum } from '@datadog/browser-rum';
import packageJson from '../../package.json';
import { getGraphQLTokens } from '../api/graphql';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';

// WebSocket connection status event name
export const WS_STATUS_EVENT = 'ws-connection-status';

// Configuration for version and GraphQL API URL
const { version } = packageJson;
const { NODE_ENV, REACT_APP_CHAMELEON_GRAPHQL_API_BASE_URL } = process.env;
const GRAPHQL_TOKENS_STORAGE_KEY = 'uta.graphQLTokens';
const AUTH_QUERY_PARAM_KEY = 'authorization';

const IS_DEV = NODE_ENV === 'development';

// Utility functions for common operations
const getStoredGraphQLTokens = () => {
  try {
    const tokensString = localStorage.getItem(GRAPHQL_TOKENS_STORAGE_KEY);
    return tokensString ? JSON.parse(tokensString) : null;
  } catch (error) {
    console.error('Error parsing GraphQL tokens:', error);
    return null;
  }
};

const buildGraphQLUrl = (isWebSocket = false) => {
  const baseUrl = REACT_APP_CHAMELEON_GRAPHQL_API_BASE_URL;
  const processedBaseUrl = isWebSocket ? baseUrl.replace(/^http(s)*:\/\//gi, 'ws$1://') : baseUrl;

  const graphQLTokens = getStoredGraphQLTokens();

  if (graphQLTokens?.jwtToken) {
    const url = new URL(processedBaseUrl);
    url.searchParams.set(AUTH_QUERY_PARAM_KEY, graphQLTokens.jwtToken);
    return url.toString();
  }

  return processedBaseUrl;
};

// Control variables for token refreshing
let isRefreshing = false;
let pendingRequests = [];

// Resolves all pending requests after the token refresh is complete
const resolvePendingRequests = () => {
  pendingRequests.forEach((callback) => callback());
  pendingRequests = [];
};

// Create the HttpLink for communication with the GraphQL API
const httpLink = new HttpLink({
  uri: () => buildGraphQLUrl(),
});

let timedOut;
let activeSocket;

const dispatchWsStatus = (connected, detail = {}) => {
  window.dispatchEvent(new CustomEvent(WS_STATUS_EVENT, { detail: { connected, ...detail } }));
};

const wsLink = new GraphQLWsLink(
  createClient({
    url: () => buildGraphQLUrl(true),
    retryAttempts: Infinity,
    shouldRetry: () => true,
    keepAlive: 10000,
    on: {
      connected: (socket) => {
        activeSocket = socket;
        datadogRum.addAction('ws_connected');
        dispatchWsStatus(true);
      },
      closed: (event) => {
        const { code, reason } = event || {};
        datadogRum.addAction('ws_disconnected', { code, reason });
        dispatchWsStatus(false, { code, reason });
      },
      error: (error) => {
        datadogRum.addError(error, { source: 'websocket' });
      },
      ping: (received) => {
        if (!received) {
          // sent
          timedOut = setTimeout(() => {
            if (activeSocket.readyState === WebSocket.OPEN) {
              activeSocket.close(4408, 'Request Timeout');
            }
          }, 5000);
        } // wait 5 seconds for the pong and then close the connection
      },
      pong: (received) => {
        if (received) {
          clearTimeout(timedOut);
        } // pong is received, clear connection close timeout
      },
    },
  })
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink
);

// Retry link configuration
const retryLink = new RetryLink({
  attempts: {
    max: 3,
  },
});

// Function to prepare authentication headers for requests
const prepareAuthHeaders = () => {
  const graphQLTokens = getStoredGraphQLTokens();

  if (graphQLTokens) {
    return {
      interface: 'phonesheet-web',
      Authorization: `Bearer ${graphQLTokens.jwtToken}`,
      'Ocp-Apim-Subscription-Key': graphQLTokens.apimToken,
    };
  }

  return {};
};

// Authentication middleware to add headers to requests
const authMiddleware = setContext(async (operation) => {
  // Check if we have GraphQL tokens, if not, fetch them first
  const graphQLTokens = getStoredGraphQLTokens();

  if (graphQLTokens) {
    if (operation?.operationName === 'Login') {
      operation.variables.currentToken = graphQLTokens.jwtToken;
    }
  } else {
    try {
      const response = await getGraphQLTokens();
      const { body: graphQLTokens } = response || {};
      if (graphQLTokens) {
        localStorage.setItem(GRAPHQL_TOKENS_STORAGE_KEY, JSON.stringify(graphQLTokens));
        if (operation?.operationName === 'Login') {
          operation.variables.currentToken = graphQLTokens.jwtToken;
        }
      }
    } catch (error) {
      console.error('Failed to fetch GraphQL tokens:', error);
    }
  }

  const headers = prepareAuthHeaders();

  const storedUserEmail = localStorage.getItem('uta.user_email');
  if (storedUserEmail) {
    headers.user_email = storedUserEmail;
  }

  const storedAzureId = localStorage.getItem('uta.azure_id');
  if (storedAzureId) {
    headers.azure_id = storedAzureId;
  }

  const storedOutlookPermissions = localStorage.getItem('uta.outlook_permissions');
  if (storedOutlookPermissions) {
    headers.outlook_permissions = storedOutlookPermissions;
  }

  return { headers };
});

// Function to handle authentication errors, specifically token refresh on 401/403 errors
const handleAuthError = (graphQLErrors, networkError, operation, forward) => {
  // Check if we have 401/403 status codes in graphQLErrors
  const gqlNotAuth = graphQLErrors?.some?.(
    (error) => error?.extensions?.statusCode === 401 || error?.extensions?.statusCode === 403
  );

  const netNotAuth = networkError?.statusCode === 401 || networkError?.statusCode === 403;

  const notAuth = !!gqlNotAuth || !!netNotAuth;

  const hasToken = localStorage.getItem(GRAPHQL_TOKENS_STORAGE_KEY);

  if (hasToken && !notAuth) {
    return null;
  }

  let retryOperation;

  if (!isRefreshing) {
    isRefreshing = true;

    // Attempt to refresh the tokens and resolve pending requests
    retryOperation = fromPromise(
      getGraphQLTokens()
        .then((response) => {
          const { body: graphQLTokens } = response || {};
          localStorage.setItem(GRAPHQL_TOKENS_STORAGE_KEY, JSON.stringify(graphQLTokens));
          resolvePendingRequests();
          return graphQLTokens;
        })
        .catch(() => {
          localStorage.removeItem(GRAPHQL_TOKENS_STORAGE_KEY);
          pendingRequests = [];
        })
        .finally((tokens) => {
          isRefreshing = false;
          return tokens;
        })
    ).filter(Boolean); // Filter out falsy values
  } else {
    // If already refreshing, wait for the process to complete
    retryOperation = fromPromise(new Promise((resolve) => pendingRequests.push(() => resolve())));
  }

  return retryOperation.flatMap(() => forward(operation));
};

// Error handling middleware for GraphQL and network errors
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (IS_DEV && graphQLErrors) {
    console.log('[GraphQL errors]:', graphQLErrors);
  }

  // Check for authentication errors in graphQLErrors (401/403 status codes)
  return handleAuthError(graphQLErrors, networkError, operation, forward);
});

// Function to create the Apollo Client instance
const apolloClient = new ApolloClient({
  version,
  name: 'phonesheet-web',
  link: from([errorLink, retryLink, authMiddleware, splitLink]),
  cache: new InMemoryCache({
    addTypename: false, // Optionally disable automatic typename addition
  }),
});

export default apolloClient;
